<template>
  <div class="home-page page-shell">
    <!-- ==================== 数据健康度横幅（仅管理员可见） ==================== -->
    <div v-if="isAdmin" class="health" :class="healthClass">
      <span class="lamp" />
      <template v-if="dash">
        <span>{{ healthLabel }}</span>
        <span class="sep">|</span>
        <span>最新交易日 <b>{{ dash.kpis.latestTradeDate || '—' }}</b></span>
        <template v-if="dash.trendKing">
          <span class="sep">|</span>
          <span>
            全市场 <b>{{ nf(dash.trendKing.universe) }}</b> 只可算 · 当日落表
            <b>{{ nf(dash.trendKing.nSignal) }}</b> 只信号
          </span>
        </template>
        <span class="sep">|</span>
        <span>待办 <b>{{ dash.kpis.todoCount }}</b> 条（高 <b>{{ dash.kpis.bySeverity.high }}</b>）</span>
        <template v-if="dash.kpis.freshnessWarn">
          <span class="sep">|</span>
          <span class="warnline">
            数据源异常 <b>{{ dash.kpis.freshnessWarn }}</b> 张：{{ dash.kpis.freshnessWarnTables.join(' / ') }}
          </span>
        </template>
      </template>
      <template v-else>
        <span>{{ loading ? '正在加载工作台…' : '工作台数据加载失败' }}</span>
        <template v-if="error">
          <span class="sep">|</span>
          <span class="warnline">{{ error }}</span>
        </template>
      </template>

      <span v-if="builtAtText" class="rt">落表生成于 {{ builtAtText }}</span>

      <button
        type="button"
        class="icon-btn"
        :disabled="!dash"
        :title="freshBtnTitle"
        @click="openDialog('fresh')"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
          <rect x="2" y="3" width="12" height="10" rx="1.5" />
          <path d="M2 6.5h12M6.5 6.5V13" />
        </svg>
        数据源明细
        <span v-if="freshWarnCount" class="dot-warn" aria-hidden="true" />
      </button>

      <button type="button" class="icon-btn" :disabled="loading" title="重新拉取首页数据" @click="load(true)">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
          <path d="M13.2 8a5.2 5.2 0 1 1-1.6-3.75" />
          <path d="M13.4 2.4v3h-3" />
        </svg>
        {{ loading ? '刷新中' : '刷新' }}
      </button>
    </div>

    <!-- ==================== 概览卡 ==================== -->
    <!--
      原先这 4 张卡用 `.hot`（浅红底 + 红描边）表示「有高危待办」，
      而筛选 chips 的**真选中态** `.fchip.on` 是同一套「浅底 + 同色系描边」
      ⇒ 第一眼像「这张卡被选中了」，于是会去点其它卡想切换，但卡片根本不是控件。
      现在两件事一起改：
        ① 有明确去向的 3 张卡做成真 <button>，点击真的跳转/定位，并带 hover + → 提示；
        ② `.hot` 改成**左侧 3px 色条**，不再借用「选中态」的视觉语言。
      第 4 张「服务进程」内容与右侧「系统状态」重复、没有独立去向，
      所以保持纯读数（不加 hover、不加 →），免得又造一个「看着能点其实没反应」的东西。
    -->
    <div class="cards">
      <button
        type="button"
        class="card act"
        :class="{ hot: hasHigh }"
        :disabled="!dash"
        :title="kpiTip.todo"
        @click="focusTodoList"
      >
        <span class="l">今日待办</span>
        <span class="v" :class="hasHigh ? 'num-hi' : ''">
          {{ dash ? dash.kpis.todoCount : '—' }}<small>条</small>
        </span>
        <span v-if="dash" class="s">
          高 <b>{{ dash.kpis.bySeverity.high }}</b> · 中 <b>{{ dash.kpis.bySeverity.mid }}</b> · 低
          <b>{{ dash.kpis.bySeverity.low }}</b> · 点击定位到待办清单
        </span>
        <span v-else class="s">—</span>
        <span class="go" aria-hidden="true">→</span>
      </button>

      <button
        type="button"
        class="card act"
        :disabled="!dash"
        :title="kpiTip.alert"
        @click="goRoute(alertRoute)"
      >
        <span class="l">未处理预警</span>
        <span class="v">
          <span :class="alertCount ? 'num-hi' : ''">{{ alertCount === null ? '—' : alertCount }}</span><small>条</small>
        </span>
        <span class="s">
          <template v-if="alertCount">
            涉及 <b>{{ dash.kpis.alert.accountsCount }}</b> 个账户 · 最新
            <b>{{ dash.kpis.alert.latestDate }}</b> · 点击去「模拟实盘」处理
          </template>
          <template v-else>无未处理预警</template>
        </span>
        <span class="go" aria-hidden="true">→</span>
      </button>

      <button
        type="button"
        class="card act"
        :disabled="!dash"
        :title="kpiTip.data"
        @click="goRoute('data-config')"
      >
        <span class="l">最新数据日</span>
        <span class="v">{{ dash && dash.kpis.latestTradeDate ? md(dash.kpis.latestTradeDate) : '—' }}</span>
        <span class="s">
          <template v-if="dash">
            当日导入 <b>{{ nf(dash.kpis.importedCount) }}</b> 只 · 库内 bars
            <b>{{ nf(dash.kpis.bars) }}</b> · 点击去「数据配置」
          </template>
          <template v-else>—</template>
        </span>
        <span class="go" aria-hidden="true">→</span>
      </button>

      <div class="card">
        <span class="l">服务进程 <span class="hint-inline">只读</span></span>
        <span class="v" :class="dash ? 'num-ok' : ''">{{ dash ? '在线' : '—' }}</span>
        <span class="s">
          <template v-if="dash">
            本进程 <b>pid {{ dash.service.pid }}</b> · 内存 <b>{{ dash.service.rssMb }} MB</b> ·
            已运行 <b>{{ uptimeText }}</b> · 同右侧「系统状态」
          </template>
          <template v-else>—</template>
        </span>
      </div>
    </div>

    <!-- ==================== 等高两栏：待办清单 | 系统状态 ==================== -->
    <div class="cols">
      <!-- ---------- 待办清单 ---------- -->
      <div ref="todoBlock" class="block">
        <div class="block-head">
          <h3>待办清单</h3>
          <span class="spacer" />
          <el-popover
            placement="bottom-end"
            width="400"
            trigger="hover"
            :open-delay="80"
            popper-class="home-todo-arch-popper"
          >
            <div class="home-todo-arch-pop">
              <div class="home-todo-arch-title">实现口径（零维护）</div>
              <p>
                <b>不建新表</b>：待办全部由已有表<b>查询派生</b>（零维护），只有「研究待办」手工登记，落在一个小 JSON 里。
              </p>
              <p>
                「已处理」复用已有字段（如 <code>sim_alert.handled</code>），不另开 <code>todo_handled</code> 表。
                每条待办的数据来源与判据见右侧「判据说明」。
              </p>
            </div>
            <button slot="reference" type="button" class="icon-btn notice-trigger" aria-label="实现口径说明">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
                <circle cx="8" cy="8" r="6.2" />
                <path d="M8 7.4v4M8 5.1v.9" />
              </svg>
            </button>
          </el-popover>
          <button type="button" class="icon-btn" title="查看每条待办的数据来源与判据" @click="openDialog('caliber')">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
              <circle cx="8" cy="8" r="6.2" />
              <path d="M8 7.4v4M8 5.1v.9" />
            </svg>
            判据说明
          </button>
          <!--
            这里原来是一个 `disabled` 的「＋ 登记研究待办」按钮。
            它**任何情况下都点不动**（不是条件禁用），等于把一个永久死控件摆在页面上：
            用户每次都要花一次注意力问「为什么点不了」。
            正解是别画它 —— 改成一个真能用的图标按钮，点开讲清「在哪登记、怎么写、怎么生效」。
          -->
          <button
            type="button"
            class="icon-btn"
            title="研究待办怎么登记（本页唯一需要手工维护的一块）"
            @click="openDialog('todoReg')"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
              <circle cx="8" cy="8" r="6.2" />
              <path d="M8 7.4v4M8 5.1v.9" />
            </svg>
            登记方式
          </button>
        </div>

        <div class="todo-filter">
          <button
            v-for="f in filters"
            :key="f.key"
            type="button"
            class="fchip"
            :class="{ on: todoFilter === f.key }"
            @click="todoFilter = f.key"
          >
            {{ f.label }}<i>{{ filterCounts[f.key] }}</i>
          </button>
        </div>

        <div class="scroll">
          <div v-if="loading && !dash" class="empty">正在加载…</div>
          <div v-else-if="error && !dash" class="empty">
            加载失败：{{ error }}
            <button type="button" class="btn sm" @click="load(true)">重试</button>
          </div>
          <div v-else-if="!filteredTodos.length" class="empty">
            {{ dash && dash.todos.length ? '该严重度下没有待办。' : '暂无待办 —— 全部闭环。' }}
          </div>

          <!-- v-for 不能与 v-else 写在同一元素上（Vue 2 里 v-for 优先级更高，会先执行而拿不到 else 语义） -->
          <template v-else>
            <div
              v-for="item in filteredTodos"
              :key="item.key"
              class="todo-row"
              :class="item.severity"
            >
              <span class="dot" :class="item.severity" />
              <div>
                <div class="t">
                  {{ item.title }}
                  <span v-if="item.manual" class="mk">手工登记</span>
                  <span v-else-if="item.severity === 'low'" class="mk">提示</span>
                </div>
                <div class="d">{{ item.detail }}</div>
              </div>
              <div class="src">
                <code>{{ item.source }}</code>
                <template v-if="item.sourceNote"><br />{{ item.sourceNote }}</template>
              </div>
              <div class="act">
                <button
                  type="button"
                  class="btn sm"
                  :class="{ primary: item.actionPrimary }"
                  :disabled="!item.route"
                  :title="item.route ? '跳转到对应页面' : '这条待办没有对应页面，需按来源说明手工处理'"
                  @click="go(item)"
                >
                  {{ item.actionLabel }}
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- ---------- 系统状态 ---------- -->
      <div class="block">
        <div class="block-head">
          <h3>系统状态</h3>
        </div>
        <div class="scroll">
          <div class="stat">
            <span class="k">本服务进程</span>
            <span class="v">
              <template v-if="dash">
                <b>pid {{ dash.service.pid }}</b> 在线
                <br />
                <span class="dim">内存 {{ dash.service.rssMb }} MB · 已运行 {{ uptimeText }}</span>
                <br />
                <span class="dim">pm2 全进程数网页读不到，需在服务器上确认</span>
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <div class="stat">
            <span class="k">趋势为王落表</span>
            <span class="v">
              <template v-if="dash && dash.trendKing">
                <b>{{ dash.trendKing.days }}</b> 个交易日
                <br />
                <span class="dim">最新 {{ dash.trendKing.tradeDate }} · {{ dash.trendKing.nSignal }} 只信号</span>
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <div class="stat">
            <span class="k">ST 覆盖</span>
            <span class="v">
              <template v-if="dash && dash.trendKing">
                <b>{{ nf(dash.trendKing.stCovered) }}</b> / {{ nf(dash.trendKing.stPool) }}
                <br />
                <span class="dim">point-in-time 回补已完成</span>
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <div class="stat">
            <span class="k">最近一次导入</span>
            <span class="v">
              <template v-if="dash && dash.syncInfo">
                {{ dash.syncInfo.runAt }}
                <br />
                <span class="dim">{{ dash.syncInfo.status }} · 失败 {{ dash.syncInfo.failCount }}</span>
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <div class="stat">
            <span class="k">最新 CSV</span>
            <span class="v">
              <template v-if="dash && dash.kpis.latestCsv">
                <code>{{ dash.kpis.latestCsv.file }}</code>
                <br />
                <span class="dim">{{ nf(dash.kpis.importedCount) }} 行 · 于 {{ dash.kpis.latestCsv.importedAt }} 入库</span>
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <div class="stat">
            <span class="k">数据日滞后</span>
            <span class="v">
              <template v-if="dash && dash.session">
                <span class="pill" :class="dash.dataLag ? 'warn' : 'good'">{{ dash.dataLag }} 个交易日</span>
                <br />
                <span class="dim">
                  {{ dash.session.ymd }} {{ dash.session.weekdayCn }} · {{ dash.session.reason || dash.session.sessionLabel }}
                </span>
                <br />
                <span v-if="dash.session.nextOpenAt" class="dim">下一开市 {{ dash.session.nextOpenAt }}</span>
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <div class="stat">
            <span class="k">两行情源当日</span>
            <span class="v">
              <template v-if="dash && dash.sourceDrift && dash.sourceDrift.date">
                <span class="pill" :class="dash.sourceDrift.tone">
                  {{ dash.sourceDrift.tone === 'good' ? '正常' : '偏离' }}
                </span>
                <br />
                <span class="dim">{{ dash.sourceDrift.text }}</span>
              </template>
              <template v-else>—</template>
            </span>
          </div>

          <div class="stat">
            <span class="k">落表同步体积</span>
            <span class="v">
              <template v-if="dumpTodo">
                {{ nf(dumpTodo.meta.rows) }} 行 ≈ {{ dumpTodo.meta.estimatedMb }} MB
                <br />
                <span class="dim">每次全量，待改增量</span>
              </template>
              <template v-else>—</template>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== 投研导航 ==================== -->
    <div class="block links-block">
      <div class="block-head">
        <h3>投研导航</h3>
      </div>
      <div class="block-body">
        <section v-for="section in quickSections" :key="section.title" class="quick-section">
          <div class="kh">{{ section.title }}</div>
          <div class="quick-links">
            <a
              v-for="item in section.items"
              :key="item.url"
              class="quick-chip"
              :href="item.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ item.name }}
            </a>
          </div>
        </section>

        <section v-for="group in linkGroups" :key="group.title" class="kgroup">
          <div class="kh">{{ group.title }}</div>
          <div class="link-grid">
            <a
              v-for="item in group.items"
              :key="item.url"
              class="link-card"
              :href="item.url"
              target="_blank"
              rel="noopener noreferrer"
              :title="item.desc"
            >
              <span class="site-name">{{ item.name }}</span>
              <span class="site-host">{{ item.host }}</span>
            </a>
          </div>
        </section>
      </div>
    </div>

    <!-- ==================== 弹窗 1：数据源明细 ==================== -->
    <div v-if="isAdmin && dialog === 'fresh'" class="overlay" @click.self="closeDialog">
      <div class="dlg" role="dialog" aria-modal="true" aria-labelledby="dlg-fresh-title">
        <div class="dlg-head">
          <h3 id="dlg-fresh-title">数据源新鲜度</h3>
          <span class="hint">每张表读 max(日期列) + COUNT(*)，不含全表扫描</span>
          <button ref="dlgClose" type="button" class="x" title="关闭" aria-label="关闭" @click="closeDialog">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
        <div class="dlg-body">
          <div class="table-wrap">
            <table class="fresh">
              <thead>
                <tr>
                  <th class="w1">表名</th>
                  <th class="w2">用途</th>
                  <th class="w1 num">最新日期</th>
                  <th class="w4 num">行数</th>
                  <th>状态</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in freshness" :key="row.table" :class="{ dim: row.tone === 'gray' }">
                  <td class="tname">{{ row.table }}</td>
                  <td>{{ row.use }}</td>
                  <td class="num">{{ row.latest || '—' }}</td>
                  <td class="num">
                    <template v-if="row.count !== null && row.count !== undefined">
                      {{ nf(row.count) }}
                      <span class="dim">{{ row.countUnit }}</span>
                    </template>
                    <template v-else>—</template>
                  </td>
                  <td>
                    <span class="pill" :class="row.tone">{{ row.status }}</span>
                    <div v-if="row.note" class="dim note">{{ row.note }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="dlg-foot">
          <b>线上空表是设计如此，不是故障</b>：<code>valuation_daily</code> / <code>hfq_daily_bar</code> /
          <code>fin_report</code> 线上 0 行属正常 —— 它们只被 <code>backtest_engine.js</code> 读取，而该模块<b>未被</b>
          <code>market-api.js</code> 引入，线上没有任何 API 能读到它们，故本页<b>不对它们报警</b>。
          唯一需要关注的是 <code>sentiment_daily</code>（本地研究表，需手工重跑）。
          <template v-if="dash && dash.sourceDrift && dash.sourceDrift.date">
            <br />
            <b>两行情源当日读数</b>：<code>{{ dash.sourceDrift.text }}</code>
          </template>
        </div>
      </div>
    </div>

    <!-- ==================== 弹窗 2：待办来源与判据 ==================== -->
    <div v-if="dialog === 'caliber'" class="overlay" @click.self="closeDialog">
      <div class="dlg" role="dialog" aria-modal="true" aria-labelledby="dlg-caliber-title">
        <div class="dlg-head">
          <h3 id="dlg-caliber-title">待办来源与判据</h3>
          <span class="hint">实现依据，逐条可核对</span>
          <button ref="dlgClose" type="button" class="x" title="关闭" aria-label="关闭" @click="closeDialog">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
        <div class="dlg-body">
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th class="w1">待办项</th>
                  <th class="w2">判据（只读查询）</th>
                  <th class="w3">严重度规则</th>
                  <th class="w4">「已处理」怎么落</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>模拟盘预警</td>
                  <td><code>SELECT COUNT(*) FROM sim_alert WHERE handled = 0</code></td>
                  <td><code>level = danger</code> → 高；<code>warn</code> → 中</td>
                  <td>已有 <code>POST /api/sim/alert/handle</code> 写 <code>handled = 1</code></td>
                </tr>
                <tr>
                  <td>待确认调仓建议</td>
                  <td>逐账户调 <code>getAccountState()</code>，取 <code>suggestions.length</code></td>
                  <td>恒为高（涉及下单动作）</td>
                  <td>建议本身不落库，成交后自然消失</td>
                </tr>
                <tr>
                  <td>滚动回测信号滞后</td>
                  <td>
                    读 <code>exports/backtest/rolling_*.json</code> 的<b>文件 mtime</b>（只 <code>stat</code> 不 parse：
                    76 个文件共 67.8 MB），取最新 mtime 与最新交易日比
                  </td>
                  <td>&gt; 5 个交易日 → 高；&gt; 2 个 → 中；≤ 2 个 → 低</td>
                  <td>不落状态，重跑后自动消失</td>
                </tr>
                <tr>
                  <td>行情源一致性</td>
                  <td>
                    近 <code>20</code> 个交易日两表「当日条数差」的<b>中位数</b>作基线，
                    再看当日偏离多少（两源口径天生不同，直接比绝对条数会永久误报）
                  </td>
                  <td>偏离 &gt; 200 → 高；&gt; 50 → 中；否则不生成待办</td>
                  <td>不落状态，消除即消失</td>
                </tr>
                <tr>
                  <td>落表重传体积</td>
                  <td><code>SELECT COUNT(*) FROM trend_king_signal</code> × 平均行宽 396 B 估算</td>
                  <td>&gt; 200 MB → 高；&gt; 20 MB → 中</td>
                  <td>不落状态，改造后消失</td>
                </tr>
                <tr>
                  <td>研究待办</td>
                  <td>读 <code>data/todos.json</code>，按 <code>done</code> 字段过滤</td>
                  <td>登记时人工指定</td>
                  <td>写 <code>todos.json</code> 的 <code>done: true</code>（本地维护，线上只读）</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="dlg-foot">
          <b>为什么不做「独立消息页」</b>：本系统<b>没有推送通道</b>（全仓无 SSE / WebSocket / socket.io，只有会话心跳
          <code>setInterval</code>），也不存在第二个角色（<code>sim_account</code> 仅 3 个账户，均属同一操作人）。
          做成消息页只能靠页面轮询，体验等同「需要手点刷新的表格」，却要多背一张表 + 一套已读未读逻辑。
          <b>本页要做的是「今天该干什么」，不是「谁给你发了消息」</b>——所以是待办，不是消息。
        </div>
      </div>
    </div>

    <!-- ==================== 弹窗 3：研究待办怎么登记 ==================== -->
    <div v-if="dialog === 'todoReg'" class="overlay" @click.self="closeDialog">
      <div class="dlg" role="dialog" aria-modal="true" aria-labelledby="dlg-todoreg-title">
        <div class="dlg-head">
          <h3 id="dlg-todoreg-title">研究待办怎么登记</h3>
          <span class="hint">本页唯一需要手工维护的一块</span>
          <button ref="dlgClose" type="button" class="x" title="关闭" aria-label="关闭" @click="closeDialog">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
        <div class="dlg-body">
          <div class="reg">
            <div class="reg-note">
              <b>为什么没有「新建」按钮</b>
              <p>
                <code>data/todos.json</code> <b>以本地为源、单向同步到线上</b>（<code>deploy.sh</code> 的 rsync
                排除 <code>data/*.db</code>，<b>但不排除</b> <code>data/*.json</code>）。
                如果线上能直接写这个文件，下次部署就会被本地版本<b>静默覆盖</b>，登记的内容会无声消失。
                所以登记入口放在本地文件里，而不是做成一个在线表单 ——
                一个写了也不算数的表单，比没有表单更糟。
              </p>
            </div>

            <div class="reg-field">
              <div class="reg-k">文件路径（相对仓库根目录）</div>
              <div class="reg-v">
                <code>{{ regPath }}</code>
                <button type="button" class="btn sm" @click="copyText(regPath, '路径')">复制路径</button>
              </div>
            </div>

            <div class="reg-field">
              <div class="reg-k">
                当前在列的手工登记 <b>{{ manualTodos.length }}</b> 条
              </div>
              <div class="reg-v">
                <span v-if="!manualTodos.length" class="dim">暂无（文件里的条目都已 done 或尚未登记）</span>
                <ul v-else class="reg-list">
                  <li v-for="t in manualTodos" :key="t.key">
                    <span class="pill" :class="t.severity">{{ t.severityLabel }}</span>
                    <span class="reg-li-t">{{ t.title }}</span>
                    <code v-if="t.route">{{ t.route }}</code>
                  </li>
                </ul>
              </div>
            </div>

            <div class="reg-field">
              <div class="reg-k">格式示例</div>
              <div class="reg-v col">
                <pre class="code">{{ regSample }}</pre>
                <button type="button" class="btn sm" @click="copyText(regSample, '示例')">复制示例</button>
              </div>
            </div>

            <div class="reg-field">
              <div class="reg-k">改完怎么生效</div>
              <div class="reg-v">
                <span>
                  本地改完保存 → 点右上「刷新」立刻能看到（本地预览）；上线随
                  <code>deploy.sh</code> 同步，或单独跑 <code>deploy/sync-daily.sh</code>。
                  标记为 <code>done: true</code> 后不再出现在待办清单里，但条目仍留在文件中便于追溯。
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="dlg-foot">
          <b><code>route</code> 必须写 App.vue 里存在的菜单 key</b>，否则会<b>静默失效</b>（落到默认菜单，不报错）。
          常用取值：<code>backtest</code>（策略回测） · <code>backtest-forecast</code> ·
          <code>trend-king</code> · <code>td9</code> · <code>sim-trading</code>（模拟实盘） ·
          <code>window-ranking</code> · <code>industry</code> · <code>data-config</code>（数据配置） ·
          <code>stock-trend</code> · <code>index-trend</code>。留空或不写 = 按钮显示为不可用并提示「需手工处理」。
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchDashboardTodo } from '../utils/dashboardApi.js'

const QUICK_SECTIONS = [
  {
    title: '行情数据',
    items: [
      { name: '行情中心', url: 'https://quote.eastmoney.com/center/' },
      { name: '全球股市', url: 'https://quote.eastmoney.com/center/qqzs.html' },
      { name: '数据中心', url: 'https://data.eastmoney.com/center/' },
      { name: '沪深港通', url: 'https://quote.eastmoney.com/center/hsgt.html' },
    ],
  },
  {
    title: '数据快捷入口',
    items: [
      { name: '融资融券', url: 'https://data.eastmoney.com/rzrq/' },
      { name: '龙虎榜单', url: 'https://data.eastmoney.com/stock/lhb.html' },
      { name: '机构调研', url: 'https://data.eastmoney.com/jgdy/' },
      { name: '财报', url: 'https://data.eastmoney.com/bbsj/' },
    ],
  },
  {
    title: '基金',
    items: [{ name: '天天基金网', url: 'https://www.1234567.com.cn/' }],
  },
]

const LINK_GROUPS = [
  {
    title: '综合信息门户',
    items: [
      { name: '东方财富网', host: 'eastmoney.com', url: 'https://www.eastmoney.com/', desc: '行情、资讯与股吧社区' },
      { name: '证券之星', host: 'stockstar.com', url: 'https://www.stockstar.com/home.htm', desc: '行情、资讯与投资数据' },
      { name: '新浪财经', host: 'finance.sina.com.cn', url: 'https://finance.sina.com.cn/', desc: '财经新闻与全球行情' },
      { name: '和讯网', host: 'hexun.com', url: 'https://www.hexun.com/', desc: '股票基金期货外汇综合信息' },
    ],
  },
  {
    title: '深度与专业报道',
    items: [
      { name: '财新网', host: 'caixin.com', url: 'https://www.caixin.com/', desc: '深度原创财经报道' },
      { name: '第一财经', host: 'yicai.com', url: 'https://www.yicai.com/', desc: '专业财经媒体与产业报道' },
      { name: '金融界', host: 'jrj.com.cn', url: 'https://www.jrj.com.cn/', desc: '数据与行业分析工具' },
    ],
  },
  {
    title: '快讯与社区',
    items: [
      { name: '财联社', host: 'cls.cn', url: 'https://www.cls.cn/', desc: '财经快讯与政策动态' },
      { name: '雪球', host: 'xueqiu.com', url: 'https://xueqiu.com/', desc: '投资者交流社区' },
    ],
  },
]

/** 健康度 level → 横幅样式与文案 */
const HEALTH_MAP = {
  ok: { cls: 'ok', label: '数据健康 正常' },
  warn: { cls: 'warn', label: '数据健康 需关注' },
  notice: { cls: 'notice', label: '数据健康 提示' },
}

/** 自动刷新间隔：与后端 5 分钟重块缓存同频，避免拉得太勤又看不到新数 */
const AUTO_REFRESH_MS = 5 * 60 * 1000

/**
 * 「研究待办怎么登记」弹窗里给人复制的示例。
 * 只暴露 items，不暴露 _readme / _schema —— 那两块是给脚本和维护者看的注释，抄进新文件只会变噪音。
 */
const TODO_SAMPLE = `{
  "items": [
    {
      "id": "my-task-2026-10",
      "severity": "mid",
      "title": "一句话标题",
      "detail": "补充说明，可留空",
      "done": false,
      "createdAt": "2026-10-01",
      "route": "backtest"
    }
  ]
}`

export default {
  name: 'HomePage',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      quickSections: QUICK_SECTIONS,
      linkGroups: LINK_GROUPS,
      dash: null,
      loading: false,
      error: '',
      todoFilter: 'all',
      filters: [
        { key: 'all', label: '全部' },
        { key: 'high', label: '高' },
        { key: 'mid', label: '中' },
        { key: 'low', label: '低' },
      ],
      dialog: null,
      lastFocus: null,
      timer: null,
    }
  },
  computed: {
    healthClass() {
      const level = (this.dash && this.dash.health && this.dash.health.level) || 'notice'
      return (HEALTH_MAP[level] || HEALTH_MAP.notice).cls
    },
    healthLabel() {
      const level = (this.dash && this.dash.health && this.dash.health.level) || 'notice'
      return (HEALTH_MAP[level] || HEALTH_MAP.notice).label
    },
    freshness() {
      return (this.dash && this.dash.freshness) || []
    },
    freshWarnCount() {
      return this.freshness.filter((f) => f.tone === 'warn').length
    },
    freshBtnTitle() {
      const n = this.freshness.length
      if (!n) return '数据源明细'
      const w = this.freshWarnCount
      return `数据源明细：${n} 张表的最新日期与行数${w ? `，其中 ${w} 张需关注` : '，全部正常'}`
    },
    builtAtText() {
      const b = this.dash && this.dash.trendKing && this.dash.trendKing.builtAt
      return b ? String(b).slice(0, 16) : ''
    },
    alertCount() {
      if (!this.dash) return null
      return this.dash.kpis.alert.count
    },
    /** 是否挂着高危待办 —— 决定「今日待办」卡左侧有没有色条 */
    hasHigh() {
      return !!(this.dash && this.dash.kpis.bySeverity.high > 0)
    },
    /** 「未处理预警」卡的去向：直接取那条待办自己的 route，取不到才兜底 */
    alertRoute() {
      const t = ((this.dash && this.dash.todos) || []).find((x) => x.source === 'sim_alert')
      return (t && t.route) || 'sim-trading'
    },
    /** 3 张可点卡的 title —— 把「点了会发生什么」写在前面，避免又一次「看着能点其实不知道点了干嘛」 */
    kpiTip() {
      const k = this.dash && this.dash.kpis
      if (!k) return { todo: '', alert: '', data: '' }
      return {
        todo: `今日待办 ${k.todoCount} 条（高 ${k.bySeverity.high}）· 点击定位到下方待办清单`,
        alert: `未处理预警 ${k.alert.count} 条 · 点击跳到「模拟实盘」`,
        data: `最新交易日 ${k.latestTradeDate || '—'} · 点击跳到「数据配置」`,
      }
    },
    /** 手工登记的研究待办（不含 done）—— 弹窗 3 里列出来，方便核对文件里到底有什么 */
    manualTodos() {
      return ((this.dash && this.dash.todos) || []).filter((t) => t.manual)
    },
    regPath() {
      return (this.dash && this.dash.researchTodosFile) || 'data/todos.json'
    },
    regSample() {
      return TODO_SAMPLE
    },
    uptimeText() {
      const s = (this.dash && this.dash.service && this.dash.service.uptimeSec) || 0
      if (s < 60) return `${s} 秒`
      if (s < 3600) return `${Math.floor(s / 60)} 分`
      if (s < 86400) return `${(s / 3600).toFixed(1)} 小时`
      return `${(s / 86400).toFixed(1)} 天`
    },
    filterCounts() {
      const out = { all: 0, high: 0, mid: 0, low: 0 }
      const todos = (this.dash && this.dash.todos) || []
      out.all = todos.length
      for (const t of todos) {
        if (out[t.severity] !== undefined) out[t.severity] += 1
      }
      return out
    },
    filteredTodos() {
      const todos = (this.dash && this.dash.todos) || []
      if (this.todoFilter === 'all') return todos
      return todos.filter((t) => t.severity === this.todoFilter)
    },
    /** 落表同步体积那条待办（系统状态里要用它的行数与体积） */
    dumpTodo() {
      const todos = (this.dash && this.dash.todos) || []
      return todos.find((t) => t.key === 'sync-payload') || null
    },
  },
  watch: {
    visible(val) {
      // 每次切回首页都拉一次：待办是「今天该干什么」，展示旧数据没有意义
      if (val) this.load()
    },
  },
  mounted() {
    this.load()
    this.timer = window.setInterval(() => {
      if (this.visible && !this.dialog) this.load()
    }, AUTO_REFRESH_MS)
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    if (this.timer) window.clearInterval(this.timer)
    document.removeEventListener('keydown', this.onKeydown)
    this.unlockScroll()
  },
  methods: {
    async load(force) {
      if (this.loading) return
      this.loading = true
      this.error = ''
      try {
        this.dash = await fetchDashboardTodo()
        if (force) this.$message && this.$message.success('已刷新')
      } catch (err) {
        this.error = (err && err.message) || '请求失败'
      } finally {
        this.loading = false
      }
    },
    go(item) {
      if (!item || !item.route) return
      this.$emit('navigate', item.route)
    },
    /** 概览卡用：直接按 route key 跳页 */
    goRoute(route) {
      if (!route) return
      this.$emit('navigate', route)
    },
    /** 概览卡用：把某个区块滚到视口顶部（区块在下方、需要滚动时才有意义） */
    focusBlock(ref) {
      const c = this.$refs[ref]
      const el = c && (c.$el || c)
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    focusTodoList() {
      // 先复位筛选，否则「点进去发现是空的」比不点还差
      this.todoFilter = 'all'
      this.$nextTick(() => this.focusBlock('todoBlock'))
    },
    /**
     * 复制到剪贴板。
     * 优先用 async clipboard（需 HTTPS 或 localhost，本项目两者都满足）；
     * 取不到就退到 textarea + execCommand —— 老浏览器 / 非安全上下文下仍能用。
     */
    copyText(text, label) {
      const okMsg = () => this.$message && this.$message.success(`已复制${label}`)
      const failMsg = () => this.$message && this.$message.warning('复制失败，请手动选中复制')
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(okMsg, failMsg)
          return
        }
      } catch (e) {
        /* 落到下面的兼容分支 */
      }
      const ta = document.createElement('textarea')
      ta.value = text
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.top = '0'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      let ok = false
      try {
        ok = document.execCommand('copy')
      } catch (e) {
        ok = false
      }
      document.body.removeChild(ta)
      if (ok) okMsg()
      else failMsg()
    },
    openDialog(name) {
      if (!this.dash) return
      this.lastFocus = document.activeElement
      this.dialog = name
      this.lockScroll()
      this.$nextTick(() => {
        const btn = this.$refs.dlgClose
        // 关闭控件是真 <button>，focus 才有意义（用 <span> 冒充时这里会静默失效）
        if (btn && btn.focus) btn.focus()
      })
    },
    closeDialog() {
      this.dialog = null
      this.unlockScroll()
      const el = this.lastFocus
      this.lastFocus = null
      if (el && el.focus) el.focus()
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.dialog) this.closeDialog()
    },
    lockScroll() {
      document.documentElement.classList.add('modal-open')
    },
    unlockScroll() {
      document.documentElement.classList.remove('modal-open')
    },
    nf(v) {
      if (v === null || v === undefined || v === '') return '—'
      const n = Number(v)
      if (!Number.isFinite(n)) return String(v)
      return n.toLocaleString('en-US')
    },
    md(date) {
      const s = String(date || '')
      return s.length >= 10 ? s.slice(5) : s || '—'
    },
  },
}
</script>

<style scoped>
/* ==================== 通用 ==================== */
.home-page {
  width: 100%;
  min-width: 0;
}

.spacer {
  flex: 1 1 auto;
}

.dim {
  color: #909399;
  font-size: 11.5px;
}

/* ==================== 健康度横幅 ==================== */
.health {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 13px;
  border: 1px solid;
}
.health.ok {
  background: #f0f9eb;
  border-color: #d6e9c6;
  color: #4b7b2c;
}
.health.warn {
  background: #fef0f0;
  border-color: #fbc4c4;
  color: #c45656;
}
.health.notice {
  background: #fdf6ec;
  border-color: #f5dab1;
  color: #b88230;
}
.health .lamp {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 8px;
  background: #e6a23c;
}
.health.ok .lamp {
  background: #67c23a;
}
.health.warn .lamp {
  background: #f56c6c;
}
.health b {
  color: #303133;
}
.health .sep {
  color: #c0c4cc;
}
.health .warnline {
  font-weight: 600;
}
.health .rt {
  margin-left: auto;
  color: #909399;
  font-size: 12px;
}

/* ==================== 图标按钮 ==================== */
.icon-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 10px 0 8px;
  border: 1px solid #dcdfe6;
  border-radius: 13px;
  background: #fff;
  color: #606266;
  font-size: 12px;
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
}
.icon-btn:hover:not(:disabled) {
  border-color: #409eff;
  color: #409eff;
}
.icon-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.icon-btn svg {
  display: block;
  flex: 0 0 auto;
}
.icon-btn .dot-warn {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f56c6c;
  border: 1.5px solid #fff;
}

/* ==================== 概览卡 ==================== */
.cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 12px 0;
}
.card {
  position: relative;
  display: block;
  width: 100%;
  /* <button> 默认居中 + 系统字体，这里必须显式改掉，否则文字会跳到中间、字体也变样 */
  text-align: left;
  font-family: inherit;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 13px 16px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
/* 只有「有明确去向」的卡才给可点的视觉（hover 提升 + 右上 →）。
   第 4 张「服务进程」是纯读数，刻意不加 —— 否则又是「看着能点其实没反应」。 */
.card.act {
  cursor: pointer;
}
.card.act:hover:not(:disabled) {
  border-color: #b3d8ff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.12);
}
.card.act:focus-visible {
  outline: 2px solid #409eff;
  outline-offset: 1px;
}
.card:disabled {
  cursor: default;
}
.card .go {
  position: absolute;
  top: 10px;
  right: 12px;
  font-size: 13px;
  line-height: 1;
  color: #c0c4cc;
  opacity: 0;
  transition: opacity 0.15s, transform 0.15s, color 0.15s;
}
.card.act:hover:not(:disabled) .go {
  opacity: 1;
  color: #409eff;
  transform: translateX(2px);
}
.card .l {
  font-size: 12.5px;
  color: #909399;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.card .hint-inline {
  font-size: 10.5px;
  line-height: 15px;
  padding: 0 5px;
  border-radius: 2px;
  background: #f4f4f5;
  color: #a8abb2;
}
.card .v {
  display: block;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
  color: #303133;
}
.card .v small {
  font-size: 13px;
  font-weight: 400;
  color: #606266;
  margin-left: 2px;
}
.card .s {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  line-height: 1.6;
}
.card .s b {
  color: #606266;
}
/*
  「有高危待办」是**状态**，不是**选中态**。
  早先这里写的是 `border-color:#fbc4c4 + background:#fef9f9`，
  而筛选 chips 的真选中态 `.fchip.on` 是同一套「浅底 + 同色系描边」
  ⇒ 两张卡看着像「选中 / 未选中」，会去点没选中的想切换，但卡片当时还不是控件。
  改左侧 3px 色条：表达「警示」，且不会跟任何选中态撞车。
  用 ::before 而不是 border-left，是为了完全避开与 hover 的 border-color 打架。
*/
.card.hot::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #f56c6c;
  border-radius: 4px 0 0 4px;
}
.card.hot .l {
  color: #c45656;
}
.num-hi {
  color: #f56c6c;
}
.num-ok {
  color: #67c23a;
}

/* ==================== 区块 ==================== */
.block {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.block-head {
  flex: 0 0 auto;
  padding: 11px 16px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
/* 两栏标题栏底边平齐（红线位置），不含左侧筛选行 */
.cols > .block > .block-head {
  min-height: 48px;
  flex-wrap: nowrap;
  box-sizing: border-box;
}
.block-head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 7px;
}
.block-head .hint {
  font-size: 12px;
  color: #909399;
}
.block-body {
  padding: 14px 16px;
}

/* 两栏等高（顶底平齐）；过长时各自内部滚动 */
.cols {
  display: flex;
  align-items: stretch;
  gap: 14px;
  margin-top: 12px;
}
.cols > .block {
  min-width: 0;
  max-height: min(640px, calc(100vh - 300px));
  overflow: hidden;
}
.cols > .block:first-child {
  flex: 2 1 0;
}
.cols > .block:last-child {
  flex: 1 1 0;
}
.cols > .block > .scroll {
  flex: 1 1 auto;
}
.scroll {
  flex: 1 1 auto;
  /* min-height: 0 不能省：flex 子项默认 min-height:auto，会撑破父级高度导致滚动条失效 */
  min-height: 0;
  overflow-y: auto;
}
.scroll::-webkit-scrollbar {
  width: 8px;
}
.scroll::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 4px;
}
.scroll::-webkit-scrollbar-thumb:hover {
  background: #c0c4cc;
}

/* ==================== 按钮 ==================== */
.btn {
  height: 28px;
  padding: 0 13px;
  border-radius: 4px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #606266;
  cursor: pointer;
  font-family: inherit;
}
.btn:hover:not(:disabled) {
  border-color: #409eff;
  color: #409eff;
}
.btn.primary {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}
.btn.primary:hover:not(:disabled) {
  background: #66b1ff;
  border-color: #66b1ff;
  color: #fff;
}
.btn.sm {
  height: 26px;
  padding: 0 10px;
  font-size: 12.5px;
}
.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* ==================== 待办清单 ==================== */
.todo-filter {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 9px 16px;
  border-bottom: 1px solid #f2f4f7;
  background: #fcfcfd;
}
.fchip {
  font-size: 12.5px;
  padding: 3px 11px;
  border-radius: 11px;
  border: 1px solid #dcdfe6;
  color: #606266;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
}
.fchip:hover {
  border-color: #409eff;
  color: #409eff;
}
.fchip.on {
  background: #ecf5ff;
  border-color: #b3d8ff;
  color: #409eff;
  font-weight: 600;
}
.fchip i {
  font-style: normal;
  color: #909399;
  margin-left: 4px;
}
.fchip.on i {
  color: #409eff;
}

.todo-row {
  display: grid;
  grid-template-columns: 6px minmax(0, 1fr) 118px 84px;
  gap: 12px;
  align-items: start;
  padding: 11px 16px 11px 13px;
  border-bottom: 1px solid #f2f4f7;
  border-left: 3px solid transparent;
}
.todo-row:last-child {
  border-bottom: 0;
}
.todo-row:hover {
  background: #f7fbff;
}
.todo-row.high {
  border-left-color: #f56c6c;
}
.todo-row.mid {
  border-left-color: #e6a23c;
}
.todo-row.low {
  border-left-color: #c0c4cc;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
}
.dot.high {
  background: #f56c6c;
}
.dot.mid {
  background: #e6a23c;
}
.dot.low {
  background: #c0c4cc;
}
.todo-row .t {
  font-size: 13.5px;
  color: #303133;
  line-height: 1.5;
}
.todo-row .t .mk {
  font-size: 10.5px;
  line-height: 15px;
  padding: 0 5px;
  border-radius: 3px;
  background: #f4f4f5;
  color: #909399;
  margin-left: 6px;
  vertical-align: 1px;
}
.todo-row .d {
  font-size: 12px;
  color: #909399;
  margin-top: 3px;
  line-height: 1.65;
  word-break: break-word;
}
.todo-row .src {
  font-size: 11.5px;
  color: #909399;
  line-height: 1.6;
  word-break: break-all;
}
.todo-row .src code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #909399;
}
.todo-row .act {
  text-align: right;
}
.empty {
  padding: 34px 16px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}
.empty .btn {
  margin-left: 8px;
}
.notice-trigger {
  color: #909399;
}
.notice-trigger:hover:not(:disabled) {
  color: #409eff;
  border-color: #c6e2ff;
  background: #ecf5ff;
}
.dlg-foot code {
  background: #f5f7fa;
  padding: 0 3px;
  border-radius: 2px;
  color: #606266;
}

/* ==================== 系统状态 ==================== */
.stat {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 16px;
  border-bottom: 1px dashed #f2f4f7;
  font-size: 12.5px;
}
.stat:last-child {
  border-bottom: 0;
}
.stat .k {
  color: #909399;
  flex: 0 0 auto;
}
.stat .v {
  color: #303133;
  text-align: right;
  min-width: 0;
  word-break: break-word;
}
.stat .v code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11.5px;
  color: #606266;
}

/* ==================== 弹窗 ==================== */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.42);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 56px 24px 40px;
  z-index: 2000;
}
.dlg {
  background: #fff;
  border-radius: 6px;
  width: min(1000px, 100%);
  max-height: calc(100vh - 96px);
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}
.dlg-head {
  flex: 0 0 auto;
  padding: 13px 18px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 10px;
}
.dlg-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.dlg-head .hint {
  font-size: 12px;
  color: #909399;
}
.dlg-head .x {
  margin-left: auto;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 4px;
  border: 0;
  background: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  cursor: pointer;
  font-family: inherit;
}
.dlg-head .x:hover {
  background: #f5f7fa;
  color: #606266;
}
.dlg-body {
  padding: 14px 18px 18px;
  overflow-y: auto;
  min-height: 0;
}
.dlg-foot {
  flex: 0 0 auto;
  padding: 11px 18px;
  border-top: 1px solid #ebeef5;
  background: #fcfcfd;
  font-size: 12px;
  color: #909399;
  line-height: 1.75;
}
.table-wrap {
  overflow-x: auto;
}

/* ---------- 弹窗 3：研究待办怎么登记 ---------- */
.reg {
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-size: 12.5px;
  color: #303133;
}
.reg-note {
  border: 1px solid #f5dab1;
  background: #fdf6ec;
  color: #8a6420;
  border-radius: 4px;
  padding: 10px 12px;
  line-height: 1.75;
}
.reg-note b {
  color: #b88230;
}
.reg-note p {
  margin: 5px 0 0;
}
.reg-note code {
  background: rgba(255, 255, 255, 0.7);
  padding: 0 3px;
  border-radius: 2px;
}
.reg-field {
  border-top: 1px dashed #f2f4f7;
  padding-top: 12px;
}
.reg-field:first-of-type {
  border-top: 0;
  padding-top: 0;
}
.reg-k {
  color: #909399;
  font-size: 12px;
  margin-bottom: 7px;
}
.reg-k b {
  color: #303133;
}
.reg-v {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  line-height: 1.75;
}
.reg-v.col {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.reg-v code {
  background: #f5f7fa;
  padding: 1px 4px;
  border-radius: 2px;
  color: #606266;
}
.reg-list {
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
}
.reg-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid #f7f8fa;
}
.reg-list li:last-child {
  border-bottom: 0;
}
.reg-li-t {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
}
.code {
  margin: 0;
  width: 100%;
  background: #f7f8fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.65;
  color: #3c4858;
  overflow-x: auto;
  white-space: pre;
  font-variant-ligatures: none;
}
.dlg table {
  border-collapse: collapse;
  width: 100%;
  font-size: 12.5px;
}
.dlg table th {
  background: #fafafa;
  color: #606266;
  font-weight: 600;
  text-align: left;
  padding: 8px 12px;
  border-bottom: 1px solid #ebeef5;
  white-space: nowrap;
}
.dlg table th.num,
.dlg table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.dlg table td {
  padding: 7px 12px;
  border-bottom: 1px solid #f2f4f7;
  color: #303133;
  vertical-align: top;
  line-height: 1.6;
}
.dlg table tbody tr:hover {
  background: #f7fbff;
}
.dlg table tbody tr.dim td {
  color: #a8abb2;
}
.dlg table th.w1 {
  width: 190px;
}
.dlg table th.w2 {
  width: 230px;
}
.dlg table th.w3 {
  width: 190px;
}
.dlg table th.w4 {
  width: 130px;
}
.tname {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #606266;
}
.dlg table td code {
  background: #f5f7fa;
  padding: 0 3px;
  border-radius: 2px;
  color: #606266;
}
.note {
  margin-top: 2px;
}
.pill {
  display: inline-block;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 9px;
  background: #f0f2f5;
  color: #606266;
  white-space: nowrap;
}
.pill.warn {
  background: #fdf6ec;
  color: #b88230;
}
.pill.good {
  background: #f0f9eb;
  color: #529b2e;
}
.pill.gray {
  background: #f4f4f5;
  color: #909399;
}
/* 严重度三档（弹窗 3 里列手工登记条目时用；与 .todo-row .dot 同色系） */
.pill.high {
  background: #fef0f0;
  color: #c45656;
}
.pill.mid {
  background: #fdf6ec;
  color: #b88230;
}
.pill.low {
  background: #f0f2f5;
  color: #909399;
}

/* ==================== 投研导航 ==================== */
.links-block {
  margin-top: 14px;
}
.quick-section {
  margin-bottom: 12px;
}
.quick-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.quick-chip {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 13px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #409eff;
  font-size: 12.5px;
  font-weight: 600;
  text-decoration: none;
}
.quick-chip:hover {
  border-color: #409eff;
}
.kgroup {
  margin-top: 14px;
}
.kh {
  font-size: 12.5px;
  color: #909399;
  margin-bottom: 7px;
}
.link-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
}
.link-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 9px 12px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
  text-decoration: none;
}
.link-card:hover {
  border-color: #409eff;
}
.site-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #303133;
}
.link-card:hover .site-name {
  color: #409eff;
}
.site-host {
  font-size: 11.5px;
  color: #909399;
}

/* ==================== 窄屏兜底 ==================== */
@media (max-width: 1180px) {
  .cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 980px) {
  .cols {
    flex-direction: column;
  }
  .cols > .block {
    flex: 1 1 auto;
    max-height: 560px;
  }
  .todo-row {
    grid-template-columns: 6px minmax(0, 1fr) 84px;
  }
  .todo-row .src {
    display: none;
  }
}
</style>

<style>
/* 弹窗打开时锁住 body 滚动（不能用 scoped：要打到 html 根元素） */
html.modal-open {
  overflow: hidden;
}

/* 项目没有全局 box-sizing 重置，本页自带一套 padding+border，必须自己收住 */
.home-page,
.home-page * {
  box-sizing: border-box;
}

/* 待办清单「实现口径」悬停弹层（挂 body，需非 scoped） */
.home-todo-arch-popper {
  padding: 12px 14px !important;
}
.home-todo-arch-pop .home-todo-arch-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}
.home-todo-arch-pop p {
  margin: 0 0 8px;
  font-size: 12.5px;
  line-height: 1.7;
  color: #606266;
}
.home-todo-arch-pop p:last-child {
  margin-bottom: 0;
}
.home-todo-arch-pop code {
  background: #f5f7fa;
  padding: 0 3px;
  border-radius: 2px;
  color: #606266;
  font-size: 12px;
}
</style>
